import type { Project } from "./projects";
import type { ProjectSegmentId } from "./projectSegments";
import { getSegmentLabel } from "./projectSegments";

export { getSegmentLabel };

export const PROJECT_TYPES = [
  { id: "product-design", label: "Product design" },
  { id: "mobile-design", label: "Mobile design" },
  { id: "web-design", label: "Web design" },
  { id: "motion-design", label: "Motion design" },
  { id: "identity", label: "Identity" },
] as const;

export const PROJECT_TECHNOLOGIES = [
  { id: "wordpress", label: "WordPress" },
  { id: "jamstack", label: "Jamstack" },
  { id: "react", label: "React" },
  { id: "vibecoding", label: "VibeCoding" },
] as const;

export type ProjectTypeId = (typeof PROJECT_TYPES)[number]["id"];
export type ProjectTechnologyId = (typeof PROJECT_TECHNOLOGIES)[number]["id"];

export function getTypeLabel(id: string): string {
  return PROJECT_TYPES.find((t) => t.id === id)?.label ?? id;
}

export function getTechnologyLabel(id: string): string {
  return PROJECT_TECHNOLOGIES.find((t) => t.id === id)?.label ?? id;
}

export interface ProjectFilterState {
  type: ProjectTypeId | null;
  technology: ProjectTechnologyId | null;
  segment: ProjectSegmentId | null;
}

export const EMPTY_PROJECT_FILTERS: ProjectFilterState = {
  type: null,
  technology: null,
  segment: null,
};

export function filterProjects(
  projects: Project[],
  filters: ProjectFilterState,
): Project[] {
  return projects.filter((project) => {
    if (filters.type && !project.types.includes(filters.type)) return false;
    if (filters.technology && !project.technologies.includes(filters.technology)) {
      return false;
    }
    if (filters.segment && !project.segments.includes(filters.segment)) {
      return false;
    }
    return true;
  });
}

export function sortProjectsNewestFirst(projects: Project[]): Project[] {
  return [...projects].sort((a, b) => b.date.getTime() - a.date.getTime());
}

/**
 * Main portfolio order: pinned priority first, then newest, demoted projects last.
 * Controls what appears before “Show more” on /ux-portfolio.
 */
export const PORTFOLIO_PRIORITY_IDS: string[] = [
  "slovakfinance",
  "pinkwhale",
  "wildpoppy",
  "alloy",
  "intobridge",
  "toonect-mobile",
  "toonect-identity",
  "superunuseful-app",
  "map4nomads",
  "remeha",
  "superunuseful",
  "industryphoto",
  // Behind first fold (“Show more”)
  "guvernante",
  "baxi",
  "bdrtg",
  "lececil",
  "vydumamky",
  "polyphonicabbys",
  "akademia-fenix",
];

/**
 * Older archive work — hidden until a second “Show more” (page 3).
 */
export const PORTFOLIO_ARCHIVE_IDS: string[] = [
  "vesmirny",
  "vesmirny-motion",
  "3dscany",
  "bejo-grill",
  "danyshop",
  "hankadanka",
  "hudbazdola",
  "kadernictvoparis",
  "mattcoffin",
  "maxinn",
  "nasi-poslanci",
  "prestige",
  "selmont",
  "subclub",
  "hubneme-ve-dvou",
  "button-systems",
  "designair",
  "east-air",
  "fwc",
  "ivaco",
  "jednotavidiek",
  "kaiserhoff",
  "lacnaubytovna",
  "matador",
  "pro-look-studio",
  "sirael-esthetic",
  "katastervpapuciach",
  "micina",
  "ejedlo",
  "niejehriech",
];

export function isPortfolioArchiveProject(id: string): boolean {
  return PORTFOLIO_ARCHIVE_IDS.includes(id);
}

export function sortPortfolioProjects(projects: Project[]): Project[] {
  const byId = new Map(projects.map((p) => [p.id, p]));
  const used = new Set<string>();
  const ordered: Project[] = [];
  const archive = new Set(PORTFOLIO_ARCHIVE_IDS);

  for (const id of PORTFOLIO_PRIORITY_IDS) {
    const project = byId.get(id);
    if (project) {
      ordered.push(project);
      used.add(id);
    }
  }

  const rest = projects
    .filter((p) => !used.has(p.id) && !archive.has(p.id))
    .sort((a, b) => b.date.getTime() - a.date.getTime());

  const archived = projects
    .filter((p) => !used.has(p.id) && archive.has(p.id))
    .sort((a, b) => b.date.getTime() - a.date.getTime());

  // Keep priority archive ids in archive-list order when present
  const archivedOrdered: Project[] = [];
  const archivedSeen = new Set<string>();
  for (const id of PORTFOLIO_ARCHIVE_IDS) {
    const project = byId.get(id);
    if (project && !used.has(id)) {
      archivedOrdered.push(project);
      archivedSeen.add(id);
    }
  }
  for (const project of archived) {
    if (!archivedSeen.has(project.id)) archivedOrdered.push(project);
  }

  return [...ordered, ...rest, ...archivedOrdered];
}

export function hasActiveFilter(filters: ProjectFilterState): boolean {
  return (
    filters.type !== null || filters.technology !== null || filters.segment !== null
  );
}
